import { useEffect, useState } from "react";
import { UserCheck, UserPlus, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, fmtDate } from "@/lib/api";

const Selected = ({ value, onClear }) => (
  <div className="flex items-center justify-between gap-3 border border-emerald-200 bg-emerald-50 rounded-lg px-4 py-3" data-testid="crew-selected">
    <div className="flex items-center gap-3">
      {value.tipo === "utente" ? <UserCheck className="h-5 w-5 text-emerald-700" /> : <Clock className="h-5 w-5 text-amber-600" />}
      <div>
        <div className="font-medium">{value.cognome} {value.nome}</div>
        <div className="text-xs text-muted-foreground">
          Nato/a il {fmtDate(value.data_nascita)} · {value.tipo === "utente" ? "Utente registrato" : "In attesa di iscrizione all'app"}
        </div>
      </div>
    </div>
    <Button type="button" variant="outline" size="sm" onClick={onClear} data-testid="crew-change-button">Cambia</Button>
  </div>
);

const NewMember = ({ value, onChange }) => {
  const set = (k) => (e) => onChange({ ...value, tipo: "nuovo", [k]: e.target.value });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 border border-amber-200 bg-amber-50/60 rounded-lg p-4" data-testid="crew-new-form">
      <p className="sm:col-span-3 text-xs text-amber-800">
        L'iscrizione di questo membro sarà confermata quando una persona con stessi nome, cognome e data di nascita si registrerà all'app.
      </p>
      <div><Label>Cognome</Label><Input value={value.cognome || ""} onChange={set("cognome")} data-testid="crew-new-cognome-input" /></div>
      <div><Label>Nome</Label><Input value={value.nome || ""} onChange={set("nome")} data-testid="crew-new-nome-input" /></div>
      <div><Label>Data di nascita</Label><Input type="date" value={value.data_nascita || ""} onChange={set("data_nascita")} data-testid="crew-new-dob-input" /></div>
    </div>
  );
};

export const CrewPicker = ({ value, onChange }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const isSelected = value && (value.user_id || value.pending_id);

  useEffect(() => {
    if (isSelected) return undefined;
    const t = setTimeout(async () => {
      if (q.trim().length < 3) {
        setResults([]);
        setSearched(false);
        return;
      }
      const { data } = await api.get("/users/search", { params: { q } });
      setResults(data);
      setSearched(true);
    }, 300);
    return () => clearTimeout(t);
  }, [q, isSelected]);

  if (isSelected) return <Selected value={value} onClear={() => { onChange(null); setQ(""); }} />;

  const pick = (r) => onChange({ ...(r.tipo === "utente" ? { user_id: r.id } : { pending_id: r.id }), nome: r.nome, cognome: r.cognome, data_nascita: r.data_nascita, tipo: r.tipo });

  return (
    <div className="space-y-3">
      {value?.tipo !== "nuovo" && (
        <>
          <Input placeholder="Cerca per cognome e nome…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="crew-search-input" />
          {results.length > 0 && (
            <div className="border rounded-lg divide-y max-h-48 overflow-y-auto" data-testid="crew-search-results">
              {results.map((r) => (
                <button type="button" key={r.id} onClick={() => pick(r)} data-testid={`crew-result-${r.id}`}
                  className="w-full text-left px-4 py-2.5 hover:bg-emerald-50 transition-colors flex justify-between items-center">
                  <span className="font-medium">{r.cognome} {r.nome}</span>
                  <span className="text-xs text-muted-foreground">
                    {r.data_nascita ? `nato/a il ${fmtDate(r.data_nascita)}` : ""}{r.tipo === "in_attesa" ? " · in attesa" : ""}
                  </span>
                </button>
              ))}
            </div>
          )}
          {searched && (
            <button type="button" onClick={() => onChange({ tipo: "nuovo", cognome: "", nome: "", data_nascita: "" })}
              className="text-sm text-emerald-700 hover:underline flex items-center gap-1.5" data-testid="crew-add-new-button">
              <UserPlus className="h-4 w-4" />
              {results.length ? "Non è nessuno di questi? Inserisci un nuovo membro" : "Nessun utente trovato: inserisci un nuovo membro"}
            </button>
          )}
        </>
      )}
      {value?.tipo === "nuovo" && (
        <>
          <NewMember value={value} onChange={onChange} />
          <button type="button" onClick={() => onChange(null)} className="text-xs text-muted-foreground hover:underline" data-testid="crew-back-to-search-button">
            Torna alla ricerca
          </button>
        </>
      )}
    </div>
  );
};
